import React from "react";
import { useDispatch, useSelector } from "react-redux";

import type { Route } from "@b2bportal/air-disruption-api";
import {
  FlightsRebookActions,
  FlightsRebookSelectors,
  type SelectedSegmentState,
} from "@b2bportal/core-flights-rebook";
import type { AppDispatch } from "@b2bportal/core-types";
import type { RebookSegmentSelectionProps } from "./types";

export const useRebookSegmentSelection = ({
  data,
}: RebookSegmentSelectionProps) => {
  const selectedSegment = useSelector(
    FlightsRebookSelectors.getSelectedSegment
  );

  const dispatch = useDispatch<AppDispatch>();
  const setSelectedSegment = React.useCallback(
    (segment: SelectedSegmentState | undefined) => {
      dispatch(FlightsRebookActions.setSelectedSegment(segment));
    },
    [dispatch]
  );

  const { airlines, airports } = React.useMemo(
    () => data.tripContext ?? { airports: {}, airlines: {} },
    [data.tripContext]
  );

  const travelersCount = React.useMemo(
    () => Object.values(data.travelers ?? {}).reduce((a, b) => a + b, 0),
    [data.travelers]
  );

  const exercisableSegmentRoutes: Partial<Record<string, Route>> =
    React.useMemo(
      () =>
        Object.fromEntries(
          (data.exercisableSegments ?? []).map(
            (segment) =>
              [
                `${segment.sliceIndex}/${segment.segmentIndex}`,
                segment.rebookRoute,
              ] as const
          )
        ),
      [data.exercisableSegments]
    );

  const selectSegment = React.useCallback(
    (segmentId: string) => {
      const route = exercisableSegmentRoutes[segmentId];
      if (route == null) return;
      const parts = segmentId.split("/").map((v) => parseInt(v));
      if (parts.every((part) => !isNaN(part) && isFinite(part))) {
        const [slice, segment] = parts;
        const resolvedSegment = data.slices[slice]?.segments[segment];
        if (resolvedSegment != null) {
          setSelectedSegment({
            selection: { slice, segment },
            route,
            segment: resolvedSegment,
          });
        }
      }
    },
    [data.slices, setSelectedSegment, exercisableSegmentRoutes]
  );

  const slices = React.useMemo(
    () =>
      data.slices.map((slice, sliceIndex) => ({
        slice,
        segments: slice.segments.map((segment, segmentIndex) => {
          const id = `${sliceIndex}/${segmentIndex}`;
          return {
            id,
            route: exercisableSegmentRoutes[id] ?? null,
            segment,
          };
        }),
      })),
    [data.slices, exercisableSegmentRoutes]
  );

  return React.useMemo(
    () => ({
      context: {
        travelersCount,
        airports,
        airlines,
        slices,
        selectedSegmentId:
          selectedSegment != null
            ? `${selectedSegment.selection.slice}/${selectedSegment.selection.segment}`
            : // When we don't have a selection, the default behavior for
              // `<RadioGroup>` will be to show the last selected radio button.
              // However, if a user had something selected and then the store
              // changed to no longer have a selected state, then the visual
              // state will no longer match the actual state.
              // To fix this, we set the `value` to a non-existent item.
              "",
      },
      handlers: {
        selectSegment,
      },
    }),
    [airlines, airports, selectSegment, selectedSegment, slices, travelersCount]
  );
};
